import { getDatabase, ref, get } from 'firebase/database';
import { getCachedData, setCachedData, buildCacheKey, getCachedDataWithVersion, isCacheValid } from '../utils/cache';

const CACHE_TTL = {
  STATIC: 7 * 24 * 60 * 60 * 1000,
  CONFIG: 24 * 60 * 60 * 1000,
  USER_SPECIFIC: 24 * 60 * 60 * 1000,
  SHORT: 5 * 60 * 1000,
};

async function fetchFromRTDB(path) {
  const rtdb = getDatabase();
  const snapshot = await get(ref(rtdb, path));
  if (!snapshot.exists()) return null;
  return snapshot.val();
}

export async function getCached(path, options = {}) {
  const {
    ttl = CACHE_TTL.STATIC,
    versionPath = null,
    transform = (v) => v,
    fallback = null,
  } = options;

  const cacheKey = buildCacheKey(path);

  if (versionPath) {
    const cached = getCachedDataWithVersion(cacheKey, ttl);
    if (cached && isCacheValid(cached, ttl)) {
      // La versión remota solo se comprueba al descargar de nuevo. Mientras
      // la entrada esté vigente, evitar una petición RTDB en cada pantalla.
      return transform(cached.data);
    }
  } else {
    const cached = getCachedData(cacheKey, ttl);
    if (cached !== null) {
      return transform(cached);
    }
  }

  try {
    const data = await fetchFromRTDB(path);
    if (data === null) return fallback;

    let version = null;
    if (versionPath) {
      try {
        const rtdb = getDatabase();
        const versionSnap = await get(ref(rtdb, versionPath));
        version = versionSnap.exists() ? versionSnap.val() : null;
      } catch {}
    }

    setCachedData(cacheKey, data, version);
    return transform(data);
  } catch (error) {
    console.warn(`[rtdbCache] Error fetching ${path}:`, error);
    return fallback;
  }
}

export async function getCachedAreas() {
  return getCached('areas', {
    ttl: CACHE_TTL.STATIC,
    versionPath: 'cache_control/areas_version',
    transform: (val) => {
      if (!val || typeof val !== 'object') return [];
      return Object.entries(val).map(([id, area]) => ({
        id: parseInt(id),
        ...area,
      }));
    },
    fallback: [],
  });
}

export async function getCachedTemas() {
  return getCached('temas', {
    ttl: CACHE_TTL.STATIC,
    versionPath: 'cache_control/temas_version',
    transform: (val) => {
      if (!val || typeof val !== 'object') return [];
      return Object.entries(val).map(([id, tema]) => ({
        id: parseInt(id),
        ...tema,
      }));
    },
    fallback: [],
  });
}

export async function getCachedPreguntasPorTema() {
  return getCached('preguntas_por_tema', {
    ttl: CACHE_TTL.STATIC,
    versionPath: 'cache_control/preguntas_updated',
    transform: (val) => {
      if (!val || typeof val !== 'object') return [];
      const preguntas = [];
      Object.entries(val).forEach(([temaId, preguntasMap]) => {
        if (!preguntasMap) return;
        if (Array.isArray(preguntasMap)) {
          preguntasMap.forEach((qData, qIdx) => {
            if (!qData || typeof qData !== 'object') return;
            const tid = qData.tema_id !== undefined && qData.tema_id !== null ? parseInt(qData.tema_id) : parseInt(temaId);
            preguntas.push({ ...qData, id: qData.id !== undefined ? qData.id : `${tid}_${qIdx}`, tema_id: tid });
          });
        } else if (typeof preguntasMap === 'object') {
          Object.entries(preguntasMap).forEach(([qId, qData]) => {
            if (!qData || typeof qData !== 'object') return;
            const tid = qData.tema_id !== undefined && qData.tema_id !== null ? parseInt(qData.tema_id) : parseInt(temaId);
            const isNamespaced = qId.startsWith('exani_') || qId.startsWith('uninav_') || qId.startsWith('sedena_') || qId.startsWith('q_');
            const finalId = qData.id || (isNamespaced ? qId : `${tid}_${qId}`);
            preguntas.push({ ...qData, id: finalId, tema_id: tid });
          });
        }
      });
      return preguntas;
    },
    fallback: [],
  });
}

export async function getCachedModelos() {
  return getCached('config/apis/modelos', {
    ttl: CACHE_TTL.CONFIG,
    transform: (val) => {
      if (!val || typeof val !== 'object') return {};
      const models = {};
      Object.entries(val).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          models[key] = {
            displayName: value.displayName || key,
            modelName: value.modelName || key,
            apiKey: value.apiKey || value.gemini || '',
            provider: value.provider || 'openrouter',
          };
        } else {
          models[key] = {
            displayName: `${key} (${value})`,
            modelName: value?.toString() || key,
            apiKey: '',
            provider: 'openrouter',
          };
        }
      });
      return models;
    },
    fallback: {},
  });
}

export async function getCachedEspecialidadesTemas(hierarchyCode, corpsCode, specialtyCode) {
  if (!hierarchyCode || !corpsCode || !specialtyCode) return [];
  const path = `especialidades_temas/${hierarchyCode}/${corpsCode}/${specialtyCode}/temas`;
  const cacheKey = buildCacheKey(`espec_${hierarchyCode}_${corpsCode}_${specialtyCode}`);

  const cached = getCachedData(cacheKey, CACHE_TTL.USER_SPECIFIC);
  if (cached !== null) {
    return cached;
  }

  try {
    const data = await fetchFromRTDB(path);
    const result = [];
    if (data && typeof data === 'object') {
      Object.entries(data).forEach(([temaId, temaObj]) => {
        if (!temaObj || !temaObj.cuestionarios) return;
        result.push({
          temaId: parseInt(temaId),
          nombre: temaObj.nombre || `Especialidad Tema ${temaId}`,
          cuestionarios: temaObj.cuestionarios,
        });
      });
    }
    setCachedData(cacheKey, result, null);
    return result;
  } catch (error) {
    console.warn(`[rtdbCache] Error fetching especialidades_temas:`, error);
    return [];
  }
}

export async function getCachedLicenseTokens(uid) {
  const path = `licencesPremium/${uid}`;
  const cacheKey = buildCacheKey(`license_${uid}`);

  const cached = getCachedData(cacheKey, CACHE_TTL.SHORT);
  if (cached !== null) {
    return cached;
  }

  try {
    const data = await fetchFromRTDB(path);
    if (!data) return null;
    const tokens = {
      tokensAvailable: data.tokensAvailable || 2000000,
      tokensUsed: data.tokensUsed || 0,
      expiresAt: data.expiresAt || data.tokenPeriodEnd || null,
    };
    setCachedData(cacheKey, tokens, null);
    return tokens;
  } catch (error) {
    console.warn(`[rtdbCache] Error fetching license for ${uid}:`, error);
    return null;
  }
}

export function invalidateCache(pattern) {
  try {
    const prefix = buildCacheKey(pattern);
    Object.keys(localStorage)
      .filter(k => k.startsWith(prefix))
      .forEach(k => localStorage.removeItem(k));
  } catch {}
}

export function clearAllCache() {
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(CACHE_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  } catch {}
}
